import React, { useEffect, useRef, useState } from "react";
import { ZoomIn, ZoomOut } from "lucide-react";

const OUT = 512;

export function AvatarCropper({ file, onCancel, onDone }: { file: File; onCancel: () => void; onDone: (blob: Blob) => void }) {
  const [img, setImg] = useState<HTMLImageElement | null>(null);
  const [zoom, setZoom] = useState(1);
  const [pos, setPos] = useState({ x: 0, y: 0 });
  const drag = useRef<{ x: number; y: number; px: number; py: number } | null>(null);
  const size = 240;

  useEffect(() => {
    const url = URL.createObjectURL(file);
    const i = new Image();
    i.onload = () => setImg(i);
    i.src = url;
    return () => URL.revokeObjectURL(url);
  }, [file]);

  if (!img) return null;
  const base = Math.max(size / img.width, size / img.height);
  const scale = base * zoom;
  const w = img.width * scale, h = img.height * scale;
  const clamp = (p: { x: number; y: number }) => ({ x: Math.min(0, Math.max(size - w, p.x)), y: Math.min(0, Math.max(size - h, p.y)) });
  const centered = (z: number) => { const s = base * z; return clamp({ x: (size - img.width * s) / 2 + (pos.x - (size - w) / 2) * (s / scale), y: (size - img.height * s) / 2 + (pos.y - (size - h) / 2) * (s / scale) }); };
  const p = clamp(pos);

  const start = (e: React.PointerEvent) => { drag.current = { x: e.clientX, y: e.clientY, px: p.x, py: p.y }; (e.target as HTMLElement).setPointerCapture(e.pointerId); };
  const move = (e: React.PointerEvent) => { if (!drag.current) return; setPos(clamp({ x: drag.current.px + e.clientX - drag.current.x, y: drag.current.py + e.clientY - drag.current.y })); };
  const end = () => { drag.current = null; };
  const setZ = (z: number) => { const nz = Math.min(4, Math.max(1, z)); setPos(centered(nz)); setZoom(nz); };

  const save = () => {
    const c = document.createElement("canvas");
    c.width = c.height = OUT;
    const ctx = c.getContext("2d")!;
    const r = OUT / size;
    ctx.drawImage(img, p.x * r, p.y * r, w * r, h * r);
    c.toBlob((b) => b && onDone(b), "image/jpeg", 0.9);
  };

  return (
    <div className="fixed inset-0 z-[60] bg-[#121110]/80 flex items-end sm:items-center justify-center p-4 pb-20 sm:pb-4" data-testid="avatar-cropper">
      <div className="bg-[#F6F4EE] text-[#1A1917] rounded-md w-full max-w-sm p-5 space-y-3 max-h-full overflow-y-auto">
        <div><p className="eyebrow">Adjust photo</p><p className="serif text-xl leading-tight mt-1">Drag to reposition, zoom to frame.</p></div>
        <div className="mx-auto relative overflow-hidden rounded-full bg-[#1A1917] touch-none select-none cursor-grab active:cursor-grabbing" style={{ width: size, height: size }}
          onPointerDown={start} onPointerMove={move} onPointerUp={end} onPointerCancel={end} data-testid="cropper-canvas">
          <img src={img.src} alt="" draggable={false} className="absolute max-w-none pointer-events-none" style={{ width: w, height: h, left: p.x, top: p.y }} />
        </div>
        <div className="flex items-center gap-3">
          <button type="button" onClick={() => setZ(zoom - 0.25)} className="p-1.5 rounded-full hover:bg-[#EFECE4]" aria-label="Zoom out" data-testid="cropper-zoom-out"><ZoomOut size={16} /></button>
          <input type="range" min={1} max={4} step={0.01} value={zoom} onChange={(e) => setZ(+e.target.value)} className="flex-1 accent-[#581C25]" aria-label="Zoom" data-testid="cropper-zoom" />
          <button type="button" onClick={() => setZ(zoom + 0.25)} className="p-1.5 rounded-full hover:bg-[#EFECE4]" aria-label="Zoom in" data-testid="cropper-zoom-in"><ZoomIn size={16} /></button>
        </div>
        <div className="flex justify-end gap-2">
          <button type="button" onClick={onCancel} className="btn-ghost !py-2 text-xs" data-testid="cropper-cancel">Cancel</button>
          <button type="button" onClick={save} className="btn-wine !py-2 text-xs" data-testid="cropper-save">Use photo</button>
        </div>
      </div>
    </div>
  );
}
